import React, { useState } from "react";
import { Box, Button, createStyles, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, makeStyles, Menu, MenuItem, Modal, Tooltip } from "@material-ui/core";
import { Ban, EllipsisVertical, TriangleAlert } from "lucide-react";
import ReportPostDialog from "../ReportPostDialog";
import { blockUserAPI } from "../../../api/userApi";
import { showToast } from "../../../utils/toast";

interface PostMenuButtonProps {
    profileId: string;
    typeReport?: "post" | "profile" | "comment",
    refreshConversations?: () => void
}

export const useStyles = makeStyles((theme) => createStyles({
    menu: {
        fontSize: 20,
        "& .MuiPaper-root": {
            backgroundColor: "#131313",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            // height: '53px',
        },
        "& .MuiList-root": {
            padding: "12px",
        },
        "& .MuiMenu-list": {
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "12px",
        },
        "& .MuiList-padding": {
            padding: "12px",
        },
        "& .MuiListItem-root.Mui-disabled": {
            opacity: 1,
        },
    },
    menuItemTip: {
        backgroundColor: "#FFFFFF08",
        padding: "11.25px 17.25px",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "6px",
        "&:hover": {
            backgroundColor: "#FFFFFF12",
        },
    },
    modalTipWrapper: {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "#131313",
        padding: "0px",
        width: "500px",
        borderRadius: "16px",
        zIndex: 1401, // має бути вищий за overlay
        boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
        border: "1px solid #363636",
        "& .MuiButton-label": {
            color: "#ffffff",
        },
        [theme.breakpoints.down("sm")]: {
            width: "90%",
        },
    },
    menuItemBlock: {
        color: "#FD3C84",
        "& .MuiButton-label": {
            color: "#FD3C84",
        },
    }
}))

export default function EllipsisMenuButtonProfile({
    profileId,
    typeReport = "post",
    refreshConversations
}: PostMenuButtonProps) {
    const classes = useStyles();
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
        null
    );

    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportPostId, setReportPostId] = useState<string | null>(null);
    const [blockUserId, setBlockUserId] = useState<string | null>(null); // Store user ID to block
    const [blockModalOpen, setBlockModalOpen] = useState(false); // State for block confirmation modal

    const openMenu = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

        setMenuPos({
            top: rect.bottom,
            left: rect.right,
        });
    };

    const closeMenu = () => {
        setMenuPos(null);
    };

    const handleReport = (id: string) => {
        if (!id) return
        setReportPostId(id);
        setReportDialogOpen(true);
    }


    return (
        <>
            <Tooltip title="More Options" arrow placement="top">
                <IconButton onClick={openMenu}>
                    <EllipsisVertical
                        color="#fff"
                        fill="#fff"
                        size={34}
                        style={{ strokeWidth: 1 }}
                    />
                </IconButton>
            </Tooltip>

            <Menu
                open={Boolean(menuPos)}
                onClose={() => closeMenu()}
                anchorReference="anchorPosition"
                anchorPosition={menuPos || { top: 0, left: 0 }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ style: { position: "fixed" } }}
                disablePortal={false}
                className={classes.menu}
            >
                <MenuItem
                    key="block"
                    onClick={() => {
                        setBlockUserId(
                            profileId || null
                        );
                        setBlockModalOpen(true);
                    }}
                    className={classes.menuItemTip}
                >
                    <Ban />
                    Block User
                </MenuItem>
                <MenuItem
                    key="report"
                    className={classes.menuItemTip}
                    onClick={() => {
                        closeMenu();
                        handleReport(profileId);
                    }}
                >
                    <TriangleAlert size={24} />
                    Report user
                </MenuItem>
            </Menu>

            <ReportPostDialog
                open={reportDialogOpen && !!reportPostId}
                onClose={() => {
                    setReportDialogOpen(false);
                    setReportPostId(null);
                }}
                targetId={reportPostId || ""}
                typeReport={typeReport}
            />

            <Modal
                open={blockModalOpen}
                onClose={() => setBlockModalOpen(false)}
                aria-labelledby="block-user-modal-title"
                aria-describedby="block-user-modal-description"
            >
                {/* <Box> */}
                <Box className={classes.modalTipWrapper}>
                    <DialogTitle
                        id="block-user-modal-title"
                        style={{ color: "#FE5A97" }}
                    >
                        Block User
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText
                            id="block-user-modal-description"
                            style={{ color: "#fff" }}
                        >
                            Are you sure you want to block this user? They will no
                            longer be able to send you messages.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setBlockModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                if (blockUserId) {
                                    try {
                                        await blockUserAPI(blockUserId);
                                        showToast("User blocked successfully", "success");
                                        refreshConversations && refreshConversations()
                                    } catch (error) {
                                        showToast("Failed to block user", "error");
                                    }
                                }

                                setBlockModalOpen(false);
                                setBlockUserId(null);
                            }}
                            className={classes.menuItemBlock}
                        >
                            Block
                        </Button>
                    </DialogActions>
                </Box>
            </Modal>
        </>
    );
}